import { useEffect, useMemo, useState } from 'react'
import { ApiOutlined, CheckCircleFilled, CloudServerOutlined, CloudUploadOutlined, DatabaseOutlined, LoadingOutlined, RobotOutlined } from '@ant-design/icons'
import { Alert, Badge, Button, Layout, Segmented, Select, Skeleton, Space, Tag, Typography } from 'antd'
import { api } from './api'
import { DataIngest } from './components/DataIngest'
import { Diagnostics } from './components/Diagnostics'
import { LayerPanel } from './components/LayerPanel'
import { MapCanvas } from './components/MapCanvas'
import { PointCloudCanvas } from './components/PointCloudCanvas'
import { Timeline } from './components/Timeline'
import { absoluteTimestamp, frameStepSeconds } from './lib/time'
import type {
  Capability,
  IMUAnalysis,
  IMUBucket,
  LayerVisibility,
  MapCatalogEntry,
  PlatformConfig,
  PointCloudFrame,
  ReplayFrame,
  SessionManifest,
  SessionSummary,
  StaticMapPointCloud,
} from './types'

const { Header, Content, Sider } = Layout

const defaultLayers: LayerVisibility = {
  map: true,
  history: true,
  globalPath: true,
  localPlanned: true,
  localExecuted: true,
  peerRobots: true,
  laser: true,
  infrared: true,
  sonar: true,
  localGrid: false,
  costGrid: false,
  motionGrid: false,
  strategies: true,
  trajectories: true,
}

export default function App() {
  const [sessions, setSessions] = useState<SessionSummary[]>([])
  const [capabilities, setCapabilities] = useState<Capability[]>([])
  const [platformConfig, setPlatformConfig] = useState<PlatformConfig>()
  const [mapCatalog, setMapCatalog] = useState<MapCatalogEntry[]>([])
  const [sessionID, setSessionID] = useState<string>()
  const [manifest, setManifest] = useState<SessionManifest>()
  const [frame, setFrame] = useState<ReplayFrame>()
  const [imuBuckets, setIMUBuckets] = useState<IMUBucket[]>([])
  const [imuAnalysis, setIMUAnalysis] = useState<IMUAnalysis>()
  const [offsetSeconds, setOffsetSeconds] = useState(0)
  const [playing, setPlaying] = useState(false)
  const [speed, setSpeed] = useState(1)
  const [layers, setLayers] = useState<LayerVisibility>(defaultLayers)
  const [viewMode, setViewMode] = useState<'2d' | '3d'>('2d')
  const [pointCloud, setPointCloud] = useState<PointCloudFrame>()
  const [dynamicCloudLoading, setDynamicCloudLoading] = useState(false)
  const [staticCloudLoading, setStaticCloudLoading] = useState(false)
  const [staticMapCloud, setStaticMapCloud] = useState<StaticMapPointCloud>()
  const [ingestOpen, setIngestOpen] = useState(false)
  const [error, setError] = useState<string>()

  useEffect(() => {
    const controller = new AbortController()
    Promise.all([api.sessions(controller.signal), api.capabilities(controller.signal), api.config(controller.signal), api.maps(controller.signal)])
      .then(([sessionRows, capabilityRows, config, maps]) => {
        setSessions(sessionRows)
        setCapabilities(capabilityRows)
        setPlatformConfig(config)
        setMapCatalog(maps)
        setSessionID((current) => current ?? sessionRows[0]?.id)
      })
      .catch((reason) => {
        if (reason.name !== 'AbortError') setError(reason.message)
      })
    return () => controller.abort()
  }, [])

  useEffect(() => {
    if (!sessionID) return
    setPlaying(false)
    setOffsetSeconds(0)
    setFrame(undefined)
    setManifest(undefined)
    setIMUBuckets([])
    setIMUAnalysis(undefined)
    setViewMode('2d')
    setPointCloud(undefined)
    setDynamicCloudLoading(false)
    setStaticCloudLoading(false)
    setStaticMapCloud(undefined)
    const controller = new AbortController()
    api
      .manifest(sessionID, controller.signal)
      .then((next) => {
        setManifest(next)
        return api.imu(next.session.id, next.session.startNs, next.session.endNs, 900, controller.signal)
      })
      .then((response) => {
        setIMUBuckets(response.buckets)
        setIMUAnalysis(response.analysis)
      })
      .catch((reason) => {
        if (reason.name !== 'AbortError') setError(reason.message)
      })
    return () => controller.abort()
  }, [sessionID])

  const durationSeconds = (manifest?.session.durationNs ?? 0) / 1e9
  const poseTopic = manifest?.topics.find((topic) => topic.topic === 'pose/fused')
  const frameStep = frameStepSeconds(poseTopic?.nominalHz)

  useEffect(() => {
    if (!manifest) return
    const controller = new AbortController()
    const at = absoluteTimestamp(manifest.session.startNs, offsetSeconds)
    api
      .frame(manifest.session.id, at, controller.signal)
      .then(setFrame)
      .catch((reason) => {
        if (reason.name !== 'AbortError') setError(reason.message)
      })
    return () => controller.abort()
  }, [manifest, offsetSeconds])

  const pointCloudTimestamp = frame?.selectedNs['pointcloud/3d']
  const activeMapID = frame?.pose.pose.mapId || frame?.pose3d?.pose.mapId || 0
  const activeMap = useMemo(
    () => mapCatalog.find((item) => item.catalogId === manifest?.session.mapCatalogId && item.mapId === activeMapID),
    [mapCatalog, manifest?.session.mapCatalogId, activeMapID],
  )
  useEffect(() => {
    if (!manifest || viewMode !== '3d') return
    setPointCloud(undefined)
    if (!pointCloudTimestamp) {
      setDynamicCloudLoading(false)
      return
    }
    setDynamicCloudLoading(true)
    const controller = new AbortController()
    api
      .pointCloud(manifest.session.id, pointCloudTimestamp, 100_000, controller.signal)
      .then(setPointCloud)
      .catch((reason) => {
        if (reason.name !== 'AbortError') setError(reason.message)
      })
      .finally(() => {
        if (!controller.signal.aborted) setDynamicCloudLoading(false)
      })
    return () => controller.abort()
  }, [manifest, viewMode, pointCloudTimestamp])

  useEffect(() => {
    if (viewMode !== '3d' || !activeMap?.pointCloudAvailable) {
      setStaticMapCloud(undefined)
      setStaticCloudLoading(false)
      return
    }
    const controller = new AbortController()
    setStaticCloudLoading(true)
    api
      .mapPointCloud(activeMap.catalogId, activeMap.mapId, 150_000, controller.signal)
      .then(setStaticMapCloud)
      .catch((reason) => {
        if (reason.name !== 'AbortError') setError(reason.message)
      })
      .finally(() => {
        if (!controller.signal.aborted) setStaticCloudLoading(false)
      })
    return () => controller.abort()
  }, [viewMode, activeMap])

  useEffect(() => {
    if (!playing || !manifest) return
    const timer = window.setInterval(() => {
      setOffsetSeconds((current) => {
        const next = current + 0.05 * speed
        if (next >= durationSeconds) {
          setPlaying(false)
          return durationSeconds
        }
        return next
      })
    }, 50)
    return () => window.clearInterval(timer)
  }, [playing, speed, durationSeconds, manifest])

  const selectedSession = useMemo(() => sessions.find((item) => item.id === sessionID), [sessions, sessionID])
  const hasPointCloud = (manifest?.topics.some((topic) => topic.topic === 'pointcloud/3d') ?? false) || Boolean(activeMap?.pointCloudAvailable)
  const pointCloudLoading = dynamicCloudLoading || staticCloudLoading

  const reloadAfterImport = async (preferredSessionID?: string) => {
    const [sessionRows, capabilityRows, maps] = await Promise.all([api.sessions(), api.capabilities(), api.maps()])
    setSessions(sessionRows)
    setCapabilities(capabilityRows)
    setMapCatalog(maps)
    if (preferredSessionID && sessionRows.some((item) => item.id === preferredSessionID)) setSessionID(preferredSessionID)
  }

  if (error) {
    return (
      <div className="fatal-state">
        <Alert type="error" showIcon title="YCR Studio 加载失败" description={error} action={<a onClick={() => window.location.reload()}>重新加载</a>} />
      </div>
    )
  }

  return (
    <>
      <Layout className="app-shell">
      <Header className="app-header">
        <Space size={12} className="brand">
          <div className="brand-mark">
            <RobotOutlined />
          </div>
          <div>
            <Typography.Title level={4}>YCR Studio</Typography.Title>
            <Typography.Text>机器人数据回溯平台</Typography.Text>
          </div>
        </Space>
        <div className="header-session">
          <Typography.Text type="secondary">回放会话</Typography.Text>
          <Select
            value={sessionID}
            loading={!sessions.length}
            style={{ width: 330 }}
            options={sessions.map((item) => ({ value: item.id, label: `${item.siteName} · ${item.robotId}` }))}
            onChange={setSessionID}
          />
        </div>
        <Space size={16} className="header-status">
          {platformConfig?.adminEnabled && <Button size="small" icon={<CloudUploadOutlined />} onClick={() => setIngestOpen(true)}>上传回溯数据</Button>}
          <Badge status="processing" text="Replay" />
          <Tag icon={<DatabaseOutlined />} color="geekblue">
            MCAP / YCR
          </Tag>
          <Tag icon={<CloudServerOutlined />} color={platformConfig?.storage.provider === 'tos' ? 'cyan' : 'default'}>
            {platformConfig?.storage.provider === 'tos' ? `TOS · ${platformConfig.storage.bucket}` : 'PVC 本地缓存'}
          </Tag>
        </Space>
      </Header>
      <Layout className="app-body">
        <Sider width={264} className="app-sider">
          <div className="session-card">
            <Space align="start">
              <ApiOutlined className="session-icon" />
              <div>
                <Typography.Text strong>{selectedSession?.robotId ?? '加载中'}</Typography.Text>
                <Typography.Paragraph type="secondary" ellipsis={{ rows: 2 }}>
                  {selectedSession?.siteName}
                </Typography.Paragraph>
              </div>
            </Space>
            <div className="session-meta">
              <span>数据质量</span>
              <Tag color={selectedSession?.quality === 'healthy' ? 'green' : 'gold'}>{selectedSession?.quality ?? '-'}</Tag>
            </div>
            <div className="session-meta">
              <span>时间语义</span>
              <Typography.Text code>last ≤ T</Typography.Text>
            </div>
          </div>
          <LayerPanel value={layers} onChange={setLayers} />
          <div className="capability-panel">
            <div className="panel-title">
              <Typography.Text strong>P0 / P1 能力</Typography.Text>
              <span>{capabilities.length}</span>
            </div>
            <div className="capability-list">
              {capabilities.map((item) => (
                <div className="capability-row" key={item.id}>
                  <Space size={8}>
                    <CheckCircleFilled className="capability-ok" />
                    <span>{item.name}</span>
                  </Space>
                  <Tag variant="filled" color={item.phase === 'P0' ? 'blue' : 'purple'}>
                    {item.phase}
                  </Tag>
                </div>
              ))}
            </div>
          </div>
        </Sider>
        <Content className="app-content">
          {!manifest || !frame ? (
            <div className="loading-state">
              <LoadingOutlined spin />
              <Typography.Text>正在建立确定性回放索引…</Typography.Text>
              <Skeleton active />
            </div>
          ) : (
            <>
              <div className="workbench">
                <section className="spatial-panel">
                  <div className="view-mode">
                    <Segmented
                      value={viewMode}
                      options={[
                        { label: '2D 地图', value: '2d' },
                        { label: '3D 点云', value: '3d', disabled: !hasPointCloud },
                      ]}
                      onChange={(value) => setViewMode(value as '2d' | '3d')}
                    />
                  </div>
                  {viewMode === '3d' ? (
                    <PointCloudCanvas
                      cloud={pointCloud}
                      staticMap={activeMap}
                      staticCloud={staticMapCloud}
                      loading={pointCloudLoading}
                      frameAvailable={Boolean(pointCloudTimestamp || activeMap?.pointCloudAvailable)}
                    />
                  ) : (
                    <MapCanvas
                      frame={frame}
                      manifest={manifest.map}
                      mapCatalogID={manifest.session.mapCatalogId}
                      staticMap={activeMap}
                      visibility={layers}
                    />
                  )}
                </section>
                <aside className="diagnostics-panel">
                  <Diagnostics manifest={manifest} frame={frame} imuBuckets={imuBuckets} imuAnalysis={imuAnalysis} />
                </aside>
              </div>
              <Timeline
                offsetSeconds={offsetSeconds}
                durationSeconds={durationSeconds}
                playing={playing}
                speed={speed}
                speeds={manifest.speeds}
                frameStep={frameStep}
                onSeek={setOffsetSeconds}
                onPlayingChange={setPlaying}
                onSpeedChange={setSpeed}
              />
            </>
          )}
        </Content>
      </Layout>
      </Layout>
      <DataIngest
        open={ingestOpen}
        config={platformConfig}
        maps={mapCatalog}
        onClose={() => setIngestOpen(false)}
        onImported={async (job) => reloadAfterImport(job.sessions[0]?.id)}
      />
    </>
  )
}
